import { Suspense, lazy } from "react"
import { createFileRoute } from "@tanstack/react-router"
// The `kinds` subpath, never the package root: the root drags
// `product-visual.tsx` → `template-registry.ts` → `fonts.ts` →
// `fonts/inter.ts` into this chunk for a string-union constant (issue #255).
import { PRODUCT_VISUAL_KIND, PRODUCT_VISUAL_KINDS } from "@nova-ai/product-visual-templates/kinds"
import { PRODUCT_VISUAL_STUDIO_STATUS, STORE_CODES, type StoreCode } from "@nova-ai/shared"
import { Grid2x2, Images, LayoutTemplate, SwatchBook } from "lucide-react"
import { z } from "zod"

import { AppShell } from "../components/layout/app-shell"
import { VisualDirectionsView } from "../components/product-visuals/visual-directions-view"
import { ProductVisualStudioWorklists } from "../components/product-visuals/product-visual-studio-worklists"
import { SegmentedControl } from "../components/ui/segmented-control"

// Studio is the store-wide surface. One simple's preview moved to the product
// page (`/products/:sku?view=visuals`, issue #251) — the product is already
// there, so nobody retypes a SKU into a form.
//
// The template harness draws the shared React templates, so it loads through a
// dynamic `import()`. Every template embeds its declared typeface as data URIs,
// and a static import would put ~294 KB of base64 font in the app's single entry
// chunk — downloaded on every dashboard page for a Studio-only feature.
const ProductVisualTemplateHarness = lazy(async () => ({
  default: (await import("../components/product-visuals/product-visual-template-harness"))
    .ProductVisualTemplateHarness,
}))

const DEFAULT_PRODUCT_VISUAL_STORE = "gymbeamsk" satisfies StoreCode

const studioSearchSchema = z.object({
  view: z.enum(["images", "templates", "visual-directions"]).optional().catch(undefined),
  store: z.enum(STORE_CODES).optional().catch(DEFAULT_PRODUCT_VISUAL_STORE),
  kind: z.enum(PRODUCT_VISUAL_KINDS).optional().catch(PRODUCT_VISUAL_KIND.nutrition),
  status: z
    .enum([
      PRODUCT_VISUAL_STUDIO_STATUS.images,
      PRODUCT_VISUAL_STUDIO_STATUS.ready,
      PRODUCT_VISUAL_STUDIO_STATUS.gaps,
      PRODUCT_VISUAL_STUDIO_STATUS.excluded,
    ])
    .optional()
    .catch(PRODUCT_VISUAL_STUDIO_STATUS.images),
  configSku: z.string().trim().max(160).optional().catch(undefined),
})

function StudioPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const view = search.view ?? "images"
  const store = search.store ?? DEFAULT_PRODUCT_VISUAL_STORE
  const visualKind = search.kind ?? PRODUCT_VISUAL_KIND.nutrition
  const status = search.status ?? PRODUCT_VISUAL_STUDIO_STATUS.images

  return (
    <AppShell
      title="Product visual Studio"
      icon={
        <span className="inline-flex size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Images className="size-4" />
        </span>
      }
      actions={
        <SegmentedControl
          label="Studio view"
          size="md"
          value={view}
          options={[
            { value: "images", label: "Images", icon: Grid2x2 },
            { value: "templates", label: "Templates", icon: LayoutTemplate },
            { value: "visual-directions", label: "Visual Directions", icon: SwatchBook },
          ]}
          onChange={(nextView) =>
            void navigate({
              search: {
                store: search.store,
                kind: search.kind,
                status: search.status,
                configSku: search.configSku,
                view: nextView === "images" ? undefined : nextView,
              },
              replace: true,
            })
          }
        />
      }
      fullWidth
    >
      {view === "templates" && (
        <Suspense fallback={<TemplateChunkLoading />}>
          <ProductVisualTemplateHarness />
        </Suspense>
      )}

      {view === "visual-directions" && (
        <VisualDirectionsView
          previewConfigSku={search.configSku ?? ""}
          onPreviewConfigSkuChange={(configSku) =>
            void navigate({
              search: {
                store: search.store,
                kind: search.kind,
                status: search.status,
                view: "visual-directions",
                configSku: configSku.trim() || undefined,
              },
              replace: true,
            })
          }
        />
      )}

      {view === "images" && (
        <ProductVisualStudioWorklists
          key={`${store}:${visualKind}:${status}`}
          store={store}
          visualKind={visualKind}
          status={status}
          onStoreChange={(nextStore) => {
            void navigate({
              search: {
                store: nextStore,
                kind: search.kind,
                status: search.status,
                view: undefined,
                configSku: search.configSku,
              },
              replace: true,
            })
          }}
          onVisualKindChange={(kind) => {
            void navigate({
              search: {
                store: search.store,
                kind,
                status: search.status,
                view: search.view,
                configSku: search.configSku,
              },
              replace: true,
            })
          }}
          onStatusChange={(nextStatus) => {
            void navigate({
              search: {
                store: search.store,
                kind: search.kind,
                status: nextStatus === PRODUCT_VISUAL_STUDIO_STATUS.images ? undefined : nextStatus,
                view: search.view,
                configSku: search.configSku,
              },
              replace: true,
            })
          }}
        />
      )}
    </AppShell>
  )
}

// Both lazy views load the same chunk — the shared React templates plus their
// embedded Inter payload — so one fallback is honest for either.
function TemplateChunkLoading() {
  return <p className="text-sm text-muted-foreground">Loading the visual templates…</p>
}

export const Route = createFileRoute("/studio")({
  validateSearch: studioSearchSchema,
  component: StudioPage,
})
